import { useFileRecordState } from "@/api/fileRecordState";
import { usePublicCanvasArtifactSafe } from "@/public-canvas/PublicCanvasArtifactContext";
import { publishedPerson } from "@/public-canvas/types";
import type { FileRecord } from "@cohesive/api-client";
import {
  createStaticBlockStateSource,
  type BlockStateSource,
} from "@cohesive/ui-components";
import { useMemo } from "react";

/** The file whose state a block shows: a reference's target. */
export function blockStateFileRecordId(
  record:
    | Pick<FileRecord, "id" | "kind" | "target_file_record_id">
    | null
    | undefined,
): string | undefined {
  if (!record?.id) return undefined;
  return record.kind === "link"
    ? (record.target_file_record_id ?? undefined)
    : record.id;
}

/**
 * `cohesive.state` for a block: live in the app, and on a published canvas
 * the snapshot frozen into canvas.json at publish time.
 */
export function useBlockStateSource(
  fileRecordId: string | undefined,
): BlockStateSource | undefined {
  const publicArtifact = usePublicCanvasArtifactSafe();
  const live = useFileRecordState(publicArtifact ? null : fileRecordId);
  const frozen =
    publicArtifact && fileRecordId
      ? publicArtifact.artifact.files[fileRecordId]?.state
      : undefined;
  const snapshot = useMemo(
    () =>
      publicArtifact && fileRecordId
        ? createStaticBlockStateSource(
            (frozen?.entries ?? []).map((e) => ({
              key: e.key,
              value: e.value,
              version: e.version,
              updatedAt: e.updated_at ?? null,
              createdBy: publishedPerson(e.created_by),
              updatedBy: publishedPerson(e.updated_by),
            })),
          )
        : undefined,
    [publicArtifact, fileRecordId, frozen],
  );
  return publicArtifact ? snapshot : live;
}
